/**
 * LO QUE DEBES Y LO QUE TE DEBEN, en la portada.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LAS DOS NO SE MIDEN IGUAL, Y ESO NO ES UN DESCUIDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo que **debes** tiene fecha: el alquiler vence el 5, la factura del
 * proveedor a 30 días. Así que se ordena por lo que vence antes, y lo vencido
 * va primero y en rojo.
 *
 * Lo que **te deben** NO tiene fecha en Softfi, y es a propósito. En una bodega
 * nadie firma un pagaré con vencimiento: se fía y se cobra cuando el cliente
 * vuelve. Inventar una fecha de vencimiento sería pedirle al dueño un dato que
 * no tiene para poder enseñarle una columna bonita.
 *
 * Lo que sí existe, y es lo que de verdad decide si le sigues dando crédito a
 * alguien, es la ANTIGÜEDAD: quien debe poco desde hace tres meses es peor
 * cliente que quien debe el doble desde el martes.
 *
 * Por eso las dos tarjetas se parecen pero no dicen lo mismo. Forzarlas a un
 * molde común habría hecho que una de las dos mintiera.
 */

import { Link } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Tarjeta, TituloSeccion, cx } from "@/components/ui";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";
import type { ResumenPorPagar } from "@/lib/endpoints";
import type { ResumenCuentasPorCobrar } from "@/lib/types";

/** Cuántas filas caben sin que la tarjeta se vuelva una lista. */
const CUANTAS = 4;

// =============================================================================
//  Lo que debes
// =============================================================================

export function PorPagarPronto({
  resumen,
  formato,
}: {
  resumen: ResumenPorPagar | undefined;
  formato: FormatoMoneda;
}) {
  // Solo las que quedan por pagar y tienen fecha: una cuenta sin fecha no está
  // «próxima a vencer», está sin fecha, y mezclarlas escondería las urgentes.
  const proximas = (resumen?.cuentas ?? [])
    .filter((c) => !c.esta_pagada && c.dias_para_vencer !== null)
    .sort((a, b) => (a.dias_para_vencer ?? 0) - (b.dias_para_vencer ?? 0))
    .slice(0, CUANTAS);

  return (
    <Tarjeta>
      <TituloSeccion
        titulo="Lo que debes"
        descripcion="Lo que vence antes, primero."
        accion={
          <Link to="/por-pagar" className="text-xs font-medium text-accent hover:underline">
            Ver todas
          </Link>
        }
      />

      {proximas.length === 0 ? (
        <SinPendientes texto="No tienes cuentas con fecha por vencer." />
      ) : (
        <div className="divide-y divide-line">
          {proximas.map((c) => {
            const dias = c.dias_para_vencer ?? 0;
            const vencida = dias < 0;
            return (
              <div key={c.id} className="flex items-center gap-3 py-2.5">
                <span
                  className={cx(
                    "flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
                    vencida ? "bg-danger-soft text-danger" : "bg-surface-2 text-muted",
                  )}
                >
                  <ArrowUpRight size={15} />
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink">{c.concepto}</p>
                  <p className={cx("text-xs", vencida ? "text-danger" : "text-muted")}>
                    {cuando(dias)}
                    {c.proveedor_nombre && ` · ${c.proveedor_nombre}`}
                  </p>
                </div>

                <span className="tabular shrink-0 text-sm font-semibold text-ink">
                  {formatMoney(c.saldo, formato)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Tarjeta>
  );
}

// =============================================================================
//  Lo que te deben
// =============================================================================

export function PorCobrarPronto({
  resumen,
  formato,
}: {
  resumen: ResumenCuentasPorCobrar | undefined;
  formato: FormatoMoneda;
}) {
  // Ya vienen ordenados por antigüedad desde el servidor. Se respeta ese orden:
  // lo más viejo es lo que hay que cobrar.
  const deudores = (resumen?.clientes ?? []).slice(0, CUANTAS);
  const vencido = toNumber(resumen?.vencido_30 ?? "0");

  return (
    <Tarjeta>
      <TituloSeccion
        titulo="Lo que te deben"
        descripcion="Lo más viejo primero: es lo que menos se cobra."
        accion={
          <Link to="/credito" className="text-xs font-medium text-accent hover:underline">
            Ver todo
          </Link>
        }
      />

      {vencido > 0 && (
        <p className="mb-2 rounded-[var(--radius-control)] bg-warning-soft px-3 py-2 text-xs text-warning">
          <strong>{formatMoney(resumen!.vencido_30, formato)}</strong> en saldos vencidos (fecha de
          pago acordada o más de 30 días).
        </p>
      )}

      {deudores.length === 0 ? (
        <SinPendientes texto="No hay saldos pendientes por cobrar." />
      ) : (
        <div className="divide-y divide-line">
          {deudores.map((c) => (
            <div key={c.customer_id} className="flex items-center gap-3 py-2.5">
              <span
                className={cx(
                  "flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
                  // ⚠️ El color sale de lo VENCIDO, no del monto. Una deuda
                  // grande de ayer es normal; una pequeña de hace tres meses es
                  // dinero que probablemente ya no vuelve. Vencido = pasó la
                  // fecha acordada o, sin fecha, 30 días (2026-10-20).
                  tieneVencido(c)
                    ? "bg-warning-soft text-warning"
                    : "bg-surface-2 text-muted",
                )}
              >
                <ArrowDownLeft size={15} />
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink">{c.nombre}</p>
                <p
                  className={cx(
                    "text-xs",
                    tieneVencido(c) ? "text-warning" : "text-muted",
                  )}
                >
                  {antiguedad(c.dias_mas_viejo)}
                  {c.ventas_abiertas > 1 && ` · ${c.ventas_abiertas} ventas`}
                </p>
              </div>

              <span className="tabular shrink-0 text-sm font-semibold text-ink">
                {formatMoney(c.saldo, formato)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Tarjeta>
  );
}

// =============================================================================
//  Piezas compartidas
// =============================================================================

/**
 * El estado vacío.
 *
 * Aquí SÍ se enseña algo cuando no hay nada —al revés que la tarjeta de avisos—
 * porque estas dos van siempre en la portada y un hueco en blanco se lee como
 * «no cargó». Un tick y una frase corta dicen «está bien, no hay nada».
 */
function SinPendientes({ texto }: { texto: string }) {
  return (
    <div className="flex items-center gap-2.5 py-4 text-sm text-muted">
      <CheckCircle2 size={16} className="shrink-0 text-success" />
      {texto}
    </div>
  );
}

/** «venció hace 3 días», «vence hoy», «en 5 días». */
function cuando(dias: number): string {
  if (dias < 0) {
    const n = Math.abs(dias);
    return n === 1 ? "venció ayer" : `venció hace ${n} días`;
  }
  if (dias === 0) return "vence hoy";
  if (dias === 1) return "vence mañana";
  return `en ${dias} días`;
}

/** «desde hace 2 meses», «desde ayer». */
/** Si el cliente tiene algo vencido. Sin el dato (servidor de antes), a los 30 días. */
function tieneVencido(c: ResumenCuentasPorCobrar["clientes"][number]): boolean {
  return c.vencido != null ? toNumber(c.vencido) > 0 : c.dias_mas_viejo >= 30;
}

function antiguedad(dias: number): string {
  if (dias <= 0) return "de hoy";
  if (dias === 1) return "desde ayer";
  if (dias < 30) return `desde hace ${dias} días`;
  const meses = Math.floor(dias / 30);
  return meses === 1 ? "desde hace un mes" : `desde hace ${meses} meses`;
}
